import { listCalcVersions } from '../calcs/versions/calcVersionList';
import type { CalcVersionAvailability } from '../types/calcVersion';
import { CALC_VERSION_DISABLED_CODE } from '../types/calcVersion';

/** Text shown on admin picker options for a built combination that is turned off. */
export const NOT_LIVE_LABEL = 'Not live';

export interface TaxYearOption {
    value: number;
    label: string;
    /** True when the combination is built but turned off (admins only see these). */
    notLive: boolean;
}

/** True when the state/year has an enabled row and is built into this release. */
export function isCalcVersionEnabled(
    stateCode: string | undefined,
    year: number | undefined,
    calcVersions: CalcVersionAvailability[],
): boolean {
    if (!stateCode || year === undefined || year === null) return false;
    const built = listCalcVersions().some(v => v.stateCode === stateCode && v.year === year);
    if (!built) return false;
    return calcVersions.some(v => v.stateCode === stateCode && v.year === year && v.isEnabled);
}

/**
 * Tax year for a new case: the current calendar year when it is enabled, else the latest enabled
 * year not after it, else null (no case can be created).
 */
export function getDefaultTaxYear(
    stateCode: string,
    calcVersions: CalcVersionAvailability[],
    now: Date = new Date(),
): number | null {
    const currentYear = now.getFullYear();
    const enabledYears = listCalcVersions()
        .filter(v => v.stateCode === stateCode && v.year <= currentYear)
        .map(v => v.year)
        .filter(year => isCalcVersionEnabled(stateCode, year, calcVersions))
        .sort((a, b) => b - a);
    return enabledYears.length > 0 ? enabledYears[0] : null;
}

/**
 * Tax year picker options, newest first. Non-admins get only enabled built years. Admins get every
 * built year; turned-off ones are labeled "Not live". The case's own year is always included so the
 * picker can show the current value.
 */
export function getTaxYearOptions(
    stateCode: string,
    calcVersions: CalcVersionAvailability[],
    isAdmin: boolean,
    caseYear?: number | null,
): TaxYearOption[] {
    const options = new Map<number, TaxYearOption>();
    for (const v of listCalcVersions()) {
        if (v.stateCode !== stateCode) continue;
        const enabled = isCalcVersionEnabled(stateCode, v.year, calcVersions);
        if (!enabled && !isAdmin) continue;
        options.set(v.year, {
            value: v.year,
            label: enabled ? String(v.year) : `${v.year} (${NOT_LIVE_LABEL})`,
            notLive: !enabled,
        });
    }
    if (caseYear !== undefined && caseYear !== null && !options.has(caseYear)) {
        const built = listCalcVersions().some(v => v.stateCode === stateCode && v.year === caseYear);
        const notLive = built && !isCalcVersionEnabled(stateCode, caseYear, calcVersions);
        options.set(caseYear, {
            value: caseYear,
            label: notLive && isAdmin ? `${caseYear} (${NOT_LIVE_LABEL})` : String(caseYear),
            notLive,
        });
    }
    return Array.from(options.values()).sort((a, b) => b.value - a.value);
}

/** True for the server's 403 CalcVersionDisabled. */
export function isCalcVersionDisabledError(error: unknown): boolean {
    const e = error as { status?: number; details?: { code?: string } } | null | undefined;
    return e?.status === 403 && e?.details?.code === CALC_VERSION_DISABLED_CODE;
}

/**
 * True when a non-admin may view but not edit a case because its state/year is turned off.
 * Pre-2025 cases are excluded: they are read-only for their own reason.
 */
export function isCalcVersionLockedFor(
    stateCode: string | undefined,
    year: number | undefined,
    calcVersions: CalcVersionAvailability[],
    isAdmin: boolean,
): boolean {
    if (isAdmin) return false;
    if (!stateCode || year === undefined || year === null || year < 2025) return false;
    return !isCalcVersionEnabled(stateCode, year, calcVersions);
}
